import { mapMoney, numberOr } from "../checkout/money";
import {
  addSpan,
  formatDateAndTime,
  formatOrderDate,
  formatOrderTime,
} from "./format";
import { groupForStatus, isRatable, normalizeEnum } from "./orderStatus";

export const normalizePage = (dto) => ({
  items: Array.isArray(dto?.content) ? dto.content : [],
  total: numberOr(dto?.totalElements, 0),
  totalPages: numberOr(dto?.totalPages, 0),
  page: numberOr(dto?.number, 0),
  pageSize: numberOr(dto?.size, 0),
  isLast: dto?.last === undefined ? true : Boolean(dto.last),
});

export const mapCounts = (dto) => ({
  PENDING: numberOr(dto?.pending, 0),
  PROCESSING: numberOr(dto?.processing, 0),
  PAST: numberOr(dto?.past, 0),
});

export const supplierInitials = (name) =>
  (name || "").trim().slice(0, 4).toUpperCase();

const mapSupplier = (dto) => ({
  id: dto?.id ?? null,
  name: dto?.name || "",
  logoUrl: dto?.referenceUrlLogo || null,
  initials: supplierInitials(dto?.name),
  phoneNumber: dto?.phoneNumber || dto?.mobileNumber || null,
});

const mapMasterRef = (dto) => {
  if (!dto?.id) return null;

  return {
    id: dto.id,
    orderNumber: dto.orderNumber ? String(dto.orderNumber) : String(dto.id),
    subOrderCount: numberOr(dto.subOrderCount, 1),
    position: numberOr(dto.position),
    total: mapMoney(dto.total),
    paidAmount: mapMoney(dto.paidAmount),
  };
};

export const mapDelivery = (dto) => {
  const deliveryType = dto?.deliveryType;
  const window = dto?.deliveryDateTime || dto?.deliveryOn;

  if (window?.date) {
    return {
      scheduled: true,
      deliveryDate: formatOrderDate(window.date),
      deliveryTime: formatOrderTime(window.from),
      deliveryEtaText: null,
      deliveryLabel: formatDateAndTime(window.date, window.from),
    };
  }

  const span = deliveryType?.maxSpan ?? deliveryType?.minSpan;
  const derived = addSpan(dto?.createdAt, span, deliveryType?.spanType);

  if (derived) {
    return {
      scheduled: false,
      deliveryDate: formatOrderDate(derived),
      deliveryTime: formatOrderTime(derived),
      deliveryEtaText: null,
      deliveryLabel: formatDateAndTime(derived, derived),
    };
  }

  const etaText = deliveryType?.etaText || deliveryType?.eta || null;

  if (etaText) {
    return {
      scheduled: false,
      deliveryDate: null,
      deliveryTime: null,
      deliveryEtaText: etaText,
      deliveryLabel: etaText,
    };
  }

  return {
    scheduled: false,
    deliveryDate: null,
    deliveryTime: null,
    deliveryEtaText: null,
    deliveryLabel: "As soon as possible",
  };
};

const countItems = (cartInfo) => {
  const lines = cartInfo?.cartProducts || cartInfo?.products || [];

  return lines.reduce((total, line) => total + numberOr(line?.quantity, 0), 0);
};

export const toOrderListItem = (dto) => {
  if (!dto) return null;

  const status = normalizeEnum(dto.currentStatus || dto.status);

  return {
    id: dto.id,
    orderNumber: dto.orderNumber ? String(dto.orderNumber) : String(dto.id),
    status,
    statusGroup: normalizeEnum(dto.statusGroup) || groupForStatus(status),
    placedDate: formatOrderDate(dto.createdAt),
    placedTime: formatOrderTime(dto.createdAt),
    supplier: mapSupplier(dto.supplier),
    deliveryTypeCode: dto.deliveryType?.code || null,
    deliveryTypeLabel: dto.deliveryType?.defaultTitle || null,
    ...mapDelivery(dto),
    paidAmount:
      mapMoney(dto.paidAmount) ||
      mapMoney(dto.payableAmount) ||
      mapMoney(dto.totalAmount),
    codAmount: mapMoney(dto.codAmount),
    itemCount: countItems(dto.cartInfo),
    ratable: isRatable(status),
    rated: normalizeEnum(dto.ratingStatus) === "A" || Boolean(dto.rated),
    master: mapMasterRef(dto.masterOrder),
  };
};

const GATEWAY_LABELS = {
  COD: "Cash on Delivery",
  PAYSTACK: "Card / Bank (Paystack)",
  WALLET: "Wallet",
};

const PENDING_PAYMENT_STATUSES = ["PENDING_CLAIM", "PENDING", "CLAIM_PENDING"];

const mapPayment = (dto) => {
  const gateway = normalizeEnum(dto?.gateway);
  const status = normalizeEnum(dto?.status);

  return {
    transactionId: dto?.transactionId ?? dto?.id ?? null,
    gateway,
    gatewayLabel: dto?.gatewayLabel || GATEWAY_LABELS[gateway] || gateway || null,
    status,
    pending: PENDING_PAYMENT_STATUSES.includes(status),
    amount: mapMoney(dto?.amount),
  };
};

export const mapPayments = (dto) => {
  const source = dto?.payments ?? dto?.transactions ?? [];

  return source.map(mapPayment);
};

const mapItem = (line, index) => {
  const product = line?.product || {};
  const volume = numberOr(line?.volume);
  const customFill =
    Boolean(product.containerOption?.variableVolume) &&
    volume !== null &&
    volume !== numberOr(product.volume);

  return {
    key: `${product.id ?? "item"}-${index}`,
    quantity: numberOr(line?.quantity, 0),
    name: product.name || "",
    material: line?.containerMaterial?.name || null,
    volume,
    customFill,
    amount: mapMoney(line?.amount),
  };
};

export const mapAddress = (dto) => {
  if (!dto) return null;

  const parts = [dto.address1, dto.address2, dto.city].filter(Boolean);

  return parts.length ? parts.join(", ") : null;
};

const mapTimelineStep = (dto) => ({
  step: normalizeEnum(dto?.step || dto?.status),
  title: dto?.title || null,
  subtitle: dto?.subtitle || dto?.subTitle || null,
  occurredAt: dto?.occurredAt || dto?.statusTime || null,
  date: formatOrderDate(dto?.occurredAt || dto?.statusTime),
  time: formatOrderTime(dto?.occurredAt || dto?.statusTime),
  completed:
    dto?.completed === undefined
      ? Boolean(dto?.occurredAt || dto?.statusTime)
      : Boolean(dto.completed),
  terminal: Boolean(dto?.terminal),
});

const TERMINAL_STEPS = ["CANCELLED", "DECLINED", "REJECTED", "REFUNDED", "RETURNED"];

const DERIVED_STEPS = ["PENDING", "ACCEPTED", "DISPATCHED", "DELIVERED"];

const deriveTimeline = (statusHistory) => {
  if (!Array.isArray(statusHistory) || !statusHistory.length) return null;

  const stamps = new Map();

  statusHistory.forEach((entry) => {
    const step = normalizeEnum(entry?.status);

    if (step) stamps.set(step, entry?.statusTime || null);
  });

  const closing = TERMINAL_STEPS.find((step) => stamps.has(step));

  const reached = closing
    ? DERIVED_STEPS.filter((step) => stamps.has(step))
    : DERIVED_STEPS;

  const steps = reached.map((step) =>
    mapTimelineStep({
      step,
      statusTime: stamps.get(step) || null,
      completed: stamps.has(step),
    })
  );

  if (closing) {
    steps.push(
      mapTimelineStep({
        step: closing,
        statusTime: stamps.get(closing),
        completed: true,
        terminal: true,
      })
    );
  }

  return steps;
};

export const mapTimeline = (dto) => {
  if (Array.isArray(dto?.timeline) && dto.timeline.length) {
    return dto.timeline.map(mapTimelineStep);
  }

  return deriveTimeline(dto?.statusHistory);
};

export const toOrderDetail = (dto) => {
  if (!dto) return null;

  const base = toOrderListItem(dto);
  const cartInfo = dto.cartInfo || {};
  const lines = cartInfo.cartProducts || cartInfo.products || [];
  const payments = mapPayments(dto);

  const pendingPayment = payments.find((payment) => payment.pending);
  const codAmount = base.codAmount;

  return {
    ...base,
    items: lines.map(mapItem),
    money: {
      subTotal: mapMoney(cartInfo.productsAmount) || mapMoney(cartInfo.totalCartAmount),
      discount: mapMoney(cartInfo.couponDiscount),
      deliveryFee:
        mapMoney(dto.applicableDeliveryAmount) || mapMoney(dto.deliveryAmount),
      platformFee: mapMoney(dto.platformFee),
      tax: mapMoney(cartInfo.productsAmountVAT),
      refunded: mapMoney(dto.refundedAmount),
      total:
        mapMoney(dto.payableAmount) ||
        mapMoney(dto.paidAmount) ||
        mapMoney(dto.totalAmount),
    },
    payments,
    codDue: pendingPayment?.amount || (payments.length ? null : codAmount),
    deliveryAddressLabel: mapAddress(dto.deliveryAddress),
    deliveryAddress: dto.deliveryAddress || null,
    deliveryType: dto.deliveryType || null,
    deliveryDateTime: dto.deliveryDateTime || null,
    distanceInMeters: numberOr(dto.distanceInMeters),
    cancellable: Boolean(dto.cancellable),
    statusChangeComment: dto.statusChangeComment || null,
    statusUpdatedAt: dto.statusUpdatedAt || null,
    rating: numberOr(dto.rating),
    review: dto.review || null,
    timeline: mapTimeline(dto),
  };
};

export const toMasterOrder = (dto) => {
  if (!dto) return null;

  return {
    id: dto.id,
    orderNumber: dto.orderNumber ? String(dto.orderNumber) : String(dto.id),
    total: mapMoney(dto.total),
    paidAmount: mapMoney(dto.paidAmount),
    payments: mapPayments(dto),
    codAmount: mapMoney(dto.codAmount),
    subOrders: (dto.subOrders || []).map((sub) => ({
      id: sub.id,
      orderNumber: sub.orderNumber ? String(sub.orderNumber) : String(sub.id),
      status: normalizeEnum(sub.currentStatus || sub.status),
      position: numberOr(sub.position),
      supplier: {
        id: sub.supplier?.id ?? null,
        name: sub.supplier?.name || "",
        logoUrl: sub.supplier?.referenceUrlLogo || null,
        initials: supplierInitials(sub.supplier?.name),
      },
    })),
  };
};
